import {
  CalendarCheck2,
  CalendarClock,
  CheckCircle2,
  CircleAlert,
  ClipboardList,
  History,
  Route,
  UserRound,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  STATUS_LABELS,
  type AlunoCasoBuscaAtiva,
  type CasoBuscaAtiva,
} from '@/domains';
import { RegistrarContatoModal } from './RegistrarContatoModal';

interface AlunoBuscaDrawerProps {
  aluno: AlunoCasoBuscaAtiva | null;
  escolaId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactRegistered: (
    novoRegistro?: Record<string, unknown>,
    casoAtualizado?: CasoBuscaAtiva,
  ) => void;
}

export function AlunoBuscaDrawer({
  aluno,
  escolaId,
  open,
  onOpenChange,
  onContactRegistered,
}: AlunoBuscaDrawerProps) {
  if (!aluno) return null;

  const faltasRecentes = aluno.presencas.filter(item => !item.presente).slice(0, 5);
  const totalPresencas = aluno.presencas.filter(item => item.presente).length;
  const taxa30Dias = aluno.presencas.length
    ? (totalPresencas / aluno.presencas.length) * 100
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-slate-200 px-6 py-5 pr-12 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
              {STATUS_LABELS[aluno.caso.status]}
            </Badge>
            <Badge
              variant="outline"
              className={
                aluno.nivel === 'CRITICO'
                  ? 'border-rose-200 bg-rose-50 text-rose-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700'
              }
            >
              Risco {aluno.score}
            </Badge>
          </div>
          <SheetTitle className="mt-2 text-xl">{aluno.aluno_nome}</SheetTitle>
          <SheetDescription>{aluno.turma_nome} · contexto consolidado do caso</SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-126px)]">
          <div className="space-y-6 p-6">
            <section className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-medium text-slate-500">Frequência em 30 dias</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">
                  {taxa30Dias === null ? '—' : `${taxa30Dias.toFixed(1)}%`}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-medium text-slate-500">Tentativas registradas</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">
                  {aluno.resumo?.totalContatos || 0}
                </p>
              </div>
            </section>

            <section>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <CircleAlert className="h-4 w-4 text-rose-600" />
                Por que este caso está priorizado
              </h3>
              <div className="flex flex-wrap gap-2">
                {aluno.motivos.map(motivo => (
                  <Badge key={motivo} variant="outline" className="bg-slate-50 text-slate-700">
                    {motivo}
                  </Badge>
                ))}
              </div>
            </section>

            <section>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <CalendarCheck2 className="h-4 w-4 text-indigo-600" />
                Últimos registros de frequência
              </h3>
              {aluno.presencas.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-1.5" aria-label="Presenças dos últimos 30 dias">
                    {aluno.presencas
                      .slice()
                      .reverse()
                      .map(item => (
                        <div
                          key={item.data_chamada}
                          title={`${new Date(`${item.data_chamada}T12:00:00`).toLocaleDateString('pt-BR')}: ${
                            item.presente
                              ? 'presente'
                              : item.falta_justificada
                                ? 'falta justificada'
                                : 'falta'
                          }`}
                          className={`h-5 w-5 rounded ${
                            item.presente
                              ? 'bg-emerald-400'
                              : item.falta_justificada
                                ? 'bg-amber-400'
                                : 'bg-rose-500'
                          }`}
                        />
                      ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded bg-emerald-400" /> Presença
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded bg-amber-400" /> Justificada
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded bg-rose-500" /> Falta
                    </span>
                  </div>
                  {faltasRecentes.length > 0 && (
                    <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200">
                      {faltasRecentes.map(falta => (
                        <li key={falta.data_chamada} className="flex justify-between px-3 py-2 text-xs">
                          <span>{new Date(`${falta.data_chamada}T12:00:00`).toLocaleDateString('pt-BR')}</span>
                          <span className={falta.falta_justificada ? 'text-amber-700' : 'text-rose-700'}>
                            {falta.falta_justificada ? 'Justificada' : 'Sem justificativa'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                  Nenhum registro recente disponível.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-slate-200 p-4">
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Route className="h-4 w-4 text-indigo-600" /> Plano de recuperação
              </h3>
              <dl className="grid gap-4 text-sm sm:grid-cols-2">
                <div>
                  <dt className="flex items-center gap-1.5 text-xs text-slate-500">
                    <UserRound className="h-3.5 w-3.5" /> Responsável
                  </dt>
                  <dd className="mt-1 font-medium text-slate-800">
                    {aluno.caso.responsavel_nome || 'Não atribuído'}
                  </dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1.5 text-xs text-slate-500">
                    <CalendarClock className="h-3.5 w-3.5" /> Follow-up
                  </dt>
                  <dd className="mt-1 font-medium text-slate-800">
                    {aluno.caso.proximo_follow_up
                      ? new Date(aluno.caso.proximo_follow_up).toLocaleString('pt-BR')
                      : 'Não agendado'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Barreira principal</dt>
                  <dd className="mt-1 font-medium text-slate-800">
                    {aluno.caso.barreira_principal || 'Ainda não identificada'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Próxima ação</dt>
                  <dd className="mt-1 font-medium text-slate-800">
                    {aluno.caso.proxima_acao || 'Ainda não definida'}
                  </dd>
                </div>
              </dl>
            </section>

            <section>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <History className="h-4 w-4 text-indigo-600" /> Histórico do caso
              </h3>
              {aluno.resumo?.historico?.length ? (
                <ol className="space-y-3">
                  {aluno.resumo.historico.map((item, index) => (
                    <li key={`${item.data_contato}-${index}`} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-slate-800">{item.forma_contato}</span>
                        <span className="text-[11px] text-slate-500">
                          {new Date(item.created_at || `${item.data_contato}T12:00:00`).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        {item.justificativa_faltas}
                      </p>
                      <p className="mt-2 text-[11px] text-slate-500">
                        {item.monitor_responsavel}
                        {item.resultado_contato === 'WHATSAPP_ABERTO' && ' · WhatsApp aberto'}
                      </p>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center">
                  <ClipboardList className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-500">Nenhuma ação registrada.</p>
                </div>
              )}
            </section>

            <div className="sticky bottom-0 flex flex-col gap-2 border-t border-slate-200 bg-white py-4 sm:flex-row">
              <RegistrarContatoModal
                alunoId={aluno.aluno_id}
                nomeAluno={aluno.aluno_nome}
                escolaId={escolaId}
                caso={aluno.caso}
                onSuccess={onContactRegistered}
              />
              {aluno.caso.status === 'EM_OBSERVACAO' && (
                <Button variant="outline" disabled className="min-h-9">
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Aguardando estabilidade
                </Button>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
